import React, { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Building2,
  Users,
  UserPlus,
  Crown,
  Shield,
  User,
  Mail,
  Trash2,
  ChevronUp,
  ChevronDown,
  LogOut,
  Check,
  X,
  Clock,
  RefreshCw,
  AlertTriangle
} from "lucide-react";

const OrganizationPage = () => {
  const { user, token, refreshUser } = useAuth();
  const [organization, setOrganization] = useState(null);
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  
  // Form states
  const [newOrgName, setNewOrgName] = useState("");
  const [newOrgDescription, setNewOrgDescription] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [submitting, setSubmitting] = useState(false);

  const fetchOrganization = async () => {
    try {
      const response = await axios.get(`${API}/organizations/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrganization(response.data);
      
      if (response.data) {
        // Fetch members
        const membersResponse = await axios.get(
          `${API}/organizations/${response.data.id}/members`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMembers(membersResponse.data);
      }
    } catch (error) {
      console.error("Failed to fetch organization:", error);
    }
  };

  const fetchPendingInvites = async () => {
    try {
      const response = await axios.get(`${API}/organizations/invites/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingInvites(response.data);
    } catch (error) {
      console.error("Failed to fetch invites:", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchOrganization(), fetchPendingInvites()]);
      setLoading(false);
    };
    loadData();
  }, [token]);

  const handleCreateOrganization = async (e) => {
    e.preventDefault();
    if (!newOrgName.trim()) {
      toast.error("Organization name is required");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/organizations`,
        { name: newOrgName, description: newOrgDescription },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Organization created successfully!");
      setShowCreateOrg(false);
      setNewOrgName("");
      setNewOrgDescription("");
      await refreshUser();
      await fetchOrganization();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create organization");
    } finally {
      setSubmitting(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) {
      toast.error("Email is required");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/organizations/${organization.id}/invite`,
        { email: inviteEmail, role: inviteRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Invitation sent to ${inviteEmail}`);
      setShowInvite(false);
      setInviteEmail("");
      setInviteRole("member");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptInvite = async (inviteId) => {
    try {
      await axios.post(
        `${API}/organizations/invites/${inviteId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Joined organization successfully!");
      await refreshUser();
      await fetchOrganization();
      await fetchPendingInvites();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept invitation");
    }
  };

  const handleDeclineInvite = async (inviteId) => {
    try {
      await axios.post(
        `${API}/organizations/invites/${inviteId}/decline`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Invitation declined");
      await fetchPendingInvites();
    } catch (error) {
      toast.error("Failed to decline invitation");
    }
  };

  const handleRemoveMember = async (userId, userName) => {
    if (!window.confirm(`Remove ${userName} from the organization?`)) return;
    
    try {
      await axios.delete(
        `${API}/organizations/${organization.id}/members/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${userName} has been removed`);
      await fetchOrganization();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove member");
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.patch(
        `${API}/organizations/${organization.id}/members/${userId}/role?role=${newRole}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Role updated");
      await fetchOrganization();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const handleLeaveOrganization = async () => {
    try {
      await axios.post(
        `${API}/organizations/leave`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("You have left the organization");
      setShowLeaveConfirm(false);
      await refreshUser();
      setOrganization(null);
      setMembers([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to leave organization");
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "owner": return <Crown className="w-4 h-4 text-amber-500" />;
      case "admin": return <Shield className="w-4 h-4 text-blue-500" />;
      default: return <User className="w-4 h-4 text-slate-400" />;
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      owner: "bg-amber-100 text-amber-700 border-amber-200",
      admin: "bg-blue-100 text-blue-700 border-blue-200",
      member: "bg-slate-100 text-slate-600 border-slate-200"
    };
    return <Badge className={`text-xs ${styles[role] || styles.member}`}>{role}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Organization</h1>
          <p className="text-slate-500">Manage your organization and team members</p>
        </div>
      </div>

      {/* Pending Invitations */}
      {pendingInvites.length > 0 && (
        <Card className="border-amber-200 bg-amber-50" data-testid="pending-invites-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
              <Clock className="w-5 h-5" />
              Pending Invitations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingInvites.map((invite) => (
              <div
                key={invite.id}
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200"
              >
                <div>
                  <p className="font-medium text-slate-900">{invite.organization_name}</p>
                  <p className="text-sm text-slate-500">
                    Invited as <span className="font-medium">{invite.role}</span>
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleAcceptInvite(invite.id)}
                    data-testid={`accept-invite-${invite.id}`}
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDeclineInvite(invite.id)}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Decline
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* No Organization State */}
      {!organization && (
        <Card data-testid="no-org-card">
          <CardContent className="py-12 text-center">
            <Building2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              No Organization Yet
            </h3>
            <p className="text-slate-500 mb-6 max-w-md mx-auto">
              Create an organization to collaborate with your team. All meetings, assets, 
              and configurations will be shared with organization members.
            </p>
            <Button onClick={() => setShowCreateOrg(true)} data-testid="create-org-btn">
              <Building2 className="w-4 h-4 mr-2" />
              Create Organization
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Organization Details */}
      {organization && (
        <>
          <Card data-testid="org-details-card">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">{organization.name}</CardTitle>
                    <CardDescription>
                      {organization.description || "No description"}
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-700 border-green-200">
                    <Users className="w-3 h-3 mr-1" />
                    {organization.member_count} members
                  </Badge>
                  {user?.organization_role && getRoleBadge(user.organization_role)}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                {(user?.organization_role === "owner" || user?.organization_role === "admin") && (
                  <Button onClick={() => setShowInvite(true)} data-testid="invite-member-btn">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Invite Member
                  </Button>
                )}
                {user?.organization_role !== "owner" && (
                  <Button
                    variant="outline"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => setShowLeaveConfirm(true)}
                    data-testid="leave-org-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Leave Organization
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Members List */}
          <Card data-testid="members-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-slate-600" />
                Team Members
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {members.map((member) => (
                  <div
                    key={member.user_id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    data-testid={`member-${member.user_id}`}
                  >
                    <div className="flex items-center gap-3">
                      {getRoleIcon(member.role)}
                      <div>
                        <p className="font-medium text-slate-900">
                          {member.user_name}
                          {member.user_id === user?.id && (
                            <span className="text-slate-400 ml-2">(You)</span>
                          )}
                        </p>
                        <p className="text-sm text-slate-500">{member.user_email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getRoleBadge(member.role)}
                      
                      {/* Role management - only for owner and not for self */}
                      {user?.organization_role === "owner" && 
                       member.user_id !== user?.id && 
                       member.role !== "owner" && (
                        <div className="flex gap-1">
                          {member.role === "member" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleUpdateRole(member.user_id, "admin")}
                              title="Promote to Admin"
                            >
                              <ChevronUp className="w-4 h-4 text-blue-600" />
                            </Button>
                          )}
                          {member.role === "admin" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleUpdateRole(member.user_id, "member")}
                              title="Demote to Member"
                            >
                              <ChevronDown className="w-4 h-4 text-slate-600" />
                            </Button>
                          )}
                        </div>
                      )}
                      
                      {/* Remove member - admins can remove members, owner can remove anyone except self */}
                      {((user?.organization_role === "owner" && member.user_id !== user?.id) ||
                        (user?.organization_role === "admin" && member.role === "member")) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleRemoveMember(member.user_id, member.user_name)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Create Organization Modal */}
      <Dialog open={showCreateOrg} onOpenChange={setShowCreateOrg}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Organization</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateOrganization} className="space-y-4">
            <div>
              <Label htmlFor="orgName">Organization Name *</Label>
              <Input
                id="orgName"
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                placeholder="e.g., Acme Diagnostics Inc."
                data-testid="org-name-input"
              />
            </div>
            <div>
              <Label htmlFor="orgDescription">Description</Label>
              <Textarea
                id="orgDescription"
                value={newOrgDescription}
                onChange={(e) => setNewOrgDescription(e.target.value)}
                placeholder="Brief description of your organization..."
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateOrg(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="create-org-submit">
                {submitting ? "Creating..." : "Create Organization"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invite Member Modal */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4">
            <div>
              <Label htmlFor="inviteEmail">Email Address *</Label>
              <Input
                id="inviteEmail"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="colleague@company.com"
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <Label>Role</Label>
              <div className="flex gap-3 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="role"
                    value="member"
                    checked={inviteRole === "member"}
                    onChange={(e) => setInviteRole(e.target.value)}
                  />
                  <span className="text-sm">Member</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="role"
                    value="admin"
                    checked={inviteRole === "admin"}
                    onChange={(e) => setInviteRole(e.target.value)}
                  />
                  <span className="text-sm">Admin</span>
                </label>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Admins can invite and manage members. Members have read/write access to shared data.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowInvite(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="send-invite-btn">
                {submitting ? "Sending..." : "Send Invitation"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Leave Organization Confirmation Modal */}
      <Dialog open={showLeaveConfirm} onOpenChange={setShowLeaveConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Leave Organization?
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Are you sure you want to leave <strong>{organization?.name}</strong>? 
            You will lose access to all shared meetings, assets, and configurations.
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowLeaveConfirm(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleLeaveOrganization}
              data-testid="confirm-leave-btn"
            >
              Leave Organization
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrganizationPage;
